import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { companyInfo } from '../../utils/dataInfo';

// Helper function to get branch name
const getBranchName = (branchId, branchesData) => {
  if (!branchesData || !Array.isArray(branchesData) || !branchId) return 'Unknown';
  const branch = branchesData.find(b => parseInt(b.id) === parseInt(branchId));
  return branch ? `${branch.name} (${branch.code})` : 'Unknown';
};

// Helper function to format phone number (if needed)
const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  // Add formatting logic here if needed
  return phone;
};

// Helper function to split text into multiple lines for PDF
const splitTextIntoLines = (doc, text, maxWidth) => {
  if (!text) return [''];
  const words = text.toString().split(' ');
  const lines = [];
  let currentLine = words[0] || '';

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = doc.getTextWidth(currentLine + ' ' + word);
    if (width < maxWidth) {
      currentLine += ' ' + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  return lines;
};

// Manual table drawing function for PDF in landscape mode
const drawSuppliersTable = (doc, data, startY) => {
  const pageWidth = doc.internal.pageSize.width;
  const margin = 14;
  const rowHeight = 12;

  // Column widths for landscape mode - adjusted for all columns
  const colWidths = [15, 25, 35, 30, 45, 40, 35]; // #, Date, Name, Phone, Email, Location, Branch

  // Check if we need a new page
  let currentY = startY;
  const neededHeight = (data.length + 1) * rowHeight;
  if (currentY + neededHeight > doc.internal.pageSize.height - 20) {
    doc.addPage('landscape');
    currentY = margin;
  }

  // Draw table header
  doc.setFillColor(25, 118, 210); // Blue background for header
  doc.setTextColor(255, 255, 255); // White text for header
  doc.setFont(undefined, 'bold');

  const headers = ['#', 'Date', 'Name', 'Phone', 'Email', 'Location', 'Branch'];
  let xPos = margin;

  // Draw header background
  doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

  // Draw header text
  headers.forEach((header, index) => {
    const cellX = xPos + 2;
    doc.text(header, cellX, currentY + 7);
    xPos += colWidths[index];
  });

  // Draw header underline
  doc.setDrawColor(25, 118, 210);
  doc.setLineWidth(0.5);
  doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);

  currentY += rowHeight;

  // Draw table rows
  doc.setFont(undefined, 'normal');
  doc.setTextColor(0, 0, 0); // Black text for content

  data.forEach((row, rowIndex) => {
    // Calculate needed height for this row
    let maxLines = 1;
    const cellLines = row.map((cell, cellIndex) => {
      const text = String(cell);
      const maxWidth = colWidths[cellIndex] - 4; // Account for padding
      const lines = splitTextIntoLines(doc, text, maxWidth);
      maxLines = Math.max(maxLines, lines.length);
      return lines;
    });

    const actualRowHeight = rowHeight * maxLines;

    // Check for page break
    if (currentY + actualRowHeight > doc.internal.pageSize.height - 20) {
      doc.addPage('landscape');
      currentY = margin;
      
      // Redraw header on new page
      doc.setFillColor(25, 118, 210);
      doc.setTextColor(255, 255, 255);
      doc.setFont(undefined, 'bold');
      
      doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
      
      xPos = margin;
      headers.forEach((header, index) => {
        doc.text(header, xPos + 2, currentY + 7);
        xPos += colWidths[index];
      });
      
      doc.setDrawColor(25, 118, 210);
      doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
      
      currentY += rowHeight;
      doc.setFont(undefined, 'normal');
      doc.setTextColor(0, 0, 0);
    }
    
    // Alternate row colors
    if (rowIndex % 2 === 0) {
      doc.setFillColor(248, 248, 248); // Very light gray for even rows
    } else {
      doc.setFillColor(255, 255, 255); // White for odd rows
    }
    
    // Draw row background
    doc.rect(margin, currentY, pageWidth - (2 * margin), actualRowHeight, 'F');
    
    // Draw row text with multi-line support
    cellLines.forEach((lines, cellIndex) => {
      xPos = margin + colWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
      
      lines.forEach((line, lineIndex) => {
        const lineY = currentY + 7 + (lineIndex * rowHeight);
        doc.text(line, xPos + 2, lineY);
      });
    });
    
    currentY += actualRowHeight;
  });

  return currentY;
};

// Enhanced PDF export for Suppliers with landscape mode
export const exportToPDF = (suppliers, branchesData = [], filters = {}) => {
  try {
    // Create PDF in landscape mode
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });
    
    // Set document properties
    doc.setProperties({
      title: 'Suppliers Report',
      subject: 'Suppliers Export',
      author: companyInfo.name,
      keywords: 'suppliers, export, report',
      creator: companyInfo.name
    });
    
    // Title section
    doc.setFontSize(20);
    doc.setTextColor(25, 118, 210);
    doc.text('SUPPLIERS REPORT', doc.internal.pageSize.width / 2, 25, { align: 'center' });
    
    // Generation details
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    let currentY = 35;
    
    // Report information
    doc.text(`Report Generated: ${new Date().toLocaleDateString()}`, 14, currentY);
    currentY += 6;
    doc.text(`Total Records: ${suppliers.length}`, 14, currentY);
    currentY += 6;
    
    // Filters applied
    if (filters.name || filters.phone || filters.email || filters.location || filters.dateFrom || filters.dateTo) {
      doc.text('Filters Applied:', 14, currentY);
      currentY += 6;
      
      if (filters.name) {
        doc.text(`• Name: ${filters.name}`, 20, currentY);
        currentY += 6;
      }
      if (filters.phone) {
        doc.text(`• Phone: ${filters.phone}`, 20, currentY);
        currentY += 6;
      }
      if (filters.email) {
        doc.text(`• Email: ${filters.email}`, 20, currentY);
        currentY += 6;
      }
      if (filters.location) {
        doc.text(`• Location: ${filters.location}`, 20, currentY);
        currentY += 6;
      }
      if (filters.dateFrom) {
        doc.text(`• From Date: ${new Date(filters.dateFrom).toLocaleDateString()}`, 20, currentY);
        currentY += 6;
      }
      if (filters.dateTo) {
        doc.text(`• To Date: ${new Date(filters.dateTo).toLocaleDateString()}`, 20, currentY);
        currentY += 6;
      }
      currentY += 6;
    }
    
    // Prepare table data with all required columns
    const tableData = suppliers.map((supplier, index) => [
      (index + 1).toString(),
      new Date(supplier.created_at).toLocaleDateString(),
      supplier.name || 'N/A',
      formatPhoneNumber(supplier.phone) || 'N/A',
      supplier.email || 'N/A',
      supplier.location || 'N/A',
      getBranchName(supplier.branch_id, branchesData)
    ]);
    
    // Draw the table
    drawSuppliersTable(doc, tableData, currentY + 5);
    
    // Save the PDF
    const fileName = `suppliers_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Suppliers PDF export failed:', error);
    throw new Error('Failed to export to PDF: ' + error.message);
  }
};

// Excel export for Suppliers with all attributes
export const exportToExcel = (suppliers, branchesData = []) => {
  try {
    // Prepare data for Excel with all attributes
    const excelData = suppliers.map((supplier, index) => ({
      '#': index + 1,
      'ID': supplier.id,
      'Date': new Date(supplier.created_at).toLocaleDateString(),
      'Name': supplier.name,
      'Phone': supplier.phone,
      'Email': supplier.email,
      'Location': supplier.location,
      'Branch': getBranchName(supplier.branch_id, branchesData),
      'Created Date': new Date(supplier.created_at).toLocaleString(),
      'Updated Date': new Date(supplier.updated_at).toLocaleString()
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const maxWidth = excelData.reduce((widths, row) => {
      Object.keys(row).forEach((key, idx) => {
        const length = String(row[key]).length;
        if (!widths[idx] || length > widths[idx]) {
          widths[idx] = length;
        }
      });
      return widths;
    }, []);
    
    worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Suppliers');

    // Generate Excel file and download
    const fileName = `suppliers_export_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Suppliers Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
  }
};

// CSV export for Suppliers with all attributes
export const exportToCSV = (suppliers, branchesData = []) => {
  try {
    // Prepare headers with all attributes
    const headers = ['#', 'ID', 'Date', 'Name', 'Phone', 'Email', 'Location', 'Branch', 'Created Date', 'Updated Date'];
    
    // Prepare data rows
    const csvData = suppliers.map((supplier, index) => [
      index + 1,
      supplier.id,
      `"${new Date(supplier.created_at).toLocaleDateString()}"`,
      `"${(supplier.name || '').replace(/"/g, '""')}"`,
      `"${(supplier.phone || '').replace(/"/g, '""')}"`,
      `"${(supplier.email || '').replace(/"/g, '""')}"`,
      `"${(supplier.location || '').replace(/"/g, '""')}"`,
      `"${getBranchName(supplier.branch_id, branchesData).replace(/"/g, '""')}"`,
      `"${new Date(supplier.created_at).toLocaleString()}"`,
      `"${new Date(supplier.updated_at).toLocaleString()}"`
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    // Create and trigger download
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `suppliers_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Suppliers CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
  }
};